import { useCallback, useEffect, useState } from 'react'
import { Utensils, X, Loader2 } from 'lucide-react'
import { tablesService } from '../../../services/tablesService'
import { peopleService } from '../../../services/peopleService'
import { useSelectedPropertyId } from '../../../store/propertyStore'
import type { User, Table } from '../../../types/api.types'
import { CardOverflowMenu } from './CardOverflowMenu'
import { EmptyState } from './EmptyState'
import { AdminBanner } from './AdminBanner'

interface TableFormState {
  tableNumber: string
  assignedUserId: number | null
}

const EMPTY_FORM: TableFormState = { tableNumber: '', assignedUserId: null }

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

// A table is a number and the one waiter serving it. Nothing else lives here —
// the tab only shows for a restaurant property, so the property is whatever the
// top bar has selected.
export function TablesTab(): JSX.Element {
  const propertyId = useSelectedPropertyId()

  const [tables, setTables] = useState<Table[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editingTable, setEditingTable] = useState<Table | null>(null)
  const [form, setForm] = useState<TableFormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t) } }, [success])
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t) } }, [error])

  useEffect(() => {
    let cancelled = false
    peopleService.getAll()
      .then(all => { if (!cancelled) setUsers(all) })
      .catch(e => { if (!cancelled) setError(errorMessage(e, 'Failed to load staff')) })
    return () => { cancelled = true }
  }, [])

  const loadTables = useCallback(async () => {
    if (propertyId == null) { setTables([]); setLoading(false); return }
    setLoading(true)
    try {
      setTables(await tablesService.getAll(propertyId))
    } catch (e) {
      setError(errorMessage(e, 'Failed to load tables'))
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => { loadTables() }, [loadTables])

  // Waiting tables is a STAFF job — managers and admins are not options.
  const waiters = propertyId == null
    ? []
    : users.filter(u => u.role === 'STAFF' && u.property_ids?.includes(propertyId))

  function openCreateModal() {
    setEditingTable(null)
    setForm(EMPTY_FORM)
    setFormError(null)
    setShowModal(true)
  }

  function openEditModal(table: Table) {
    setEditingTable(table)
    setForm({ tableNumber: String(table.table_number), assignedUserId: table.assigned_user_id ?? null })
    setFormError(null)
    setShowModal(true)
  }

  async function handleSubmit() {
    const number = Number(form.tableNumber)
    if (!Number.isInteger(number) || number <= 0) {
      setFormError('Table number must be a whole number greater than 0')
      return
    }
    if (propertyId == null) { setFormError('Select a restaurant first'); return }

    setSubmitting(true)
    setFormError(null)
    try {
      if (editingTable) {
        // Explicit null unassigns. Omitting the key would silently no-op.
        await tablesService.update(editingTable.id, {
          table_number: number,
          assigned_user_id: form.assignedUserId,
        })
        setSuccess('Table updated')
      } else {
        await tablesService.create({
          property_id: propertyId,
          table_number: number,
          assigned_user_id: form.assignedUserId,
        })
        setSuccess('Table created')
      }
      setShowModal(false)
      await loadTables()
    } catch (e) {
      // 409s name the conflicting table — show the server's words, not ours.
      setFormError(errorMessage(e, 'Failed to save table'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(table: Table) {
    if (!window.confirm(`Delete table ${table.table_number}? Past orders keep their table number.`)) return
    try {
      await tablesService.delete(table.id)
      setSuccess('Table deleted')
      await loadTables()
    } catch (e) {
      setError(errorMessage(e, 'Failed to delete table'))
    }
  }

  return (
    <div>
      <AdminBanner message={error} variant="error" />
      <AdminBanner message={success} variant="success" />

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={openCreateModal}
          disabled={propertyId == null}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Add table
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-green" />
          <span className="ml-2 text-sm text-gray-500">Loading tables...</span>
        </div>
      ) : tables.length === 0 ? (
        <EmptyState icon={Utensils} message="No tables yet" hint="Add your first table to start taking voice orders" />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tables.map(t => (
            <div key={t.id} className="min-w-0 rounded-lg border border-gray-200 bg-white p-5 transition-all duration-200 hover:border-brand-green/50 hover:shadow-md">
              <div className="mb-3 flex items-start justify-between gap-2">
                <h3 className="truncate text-[15px] font-semibold text-gray-900">Table {t.table_number}</h3>
                <CardOverflowMenu onEdit={() => openEditModal(t)} onDelete={() => handleDelete(t)} />
              </div>
              <div className="flex items-center gap-2 text-[13px]">
                <span className="w-16 shrink-0 text-gray-400">Waiter:</span>
                <span className="min-w-0 flex-1 truncate text-gray-700">
                  {t.assigned_user_name ?? 'Unassigned'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="flex max-h-[90dvh] w-full max-w-md flex-col rounded-xl bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">{editingTable ? 'Edit table' : 'Add table'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-600">{formError}</div>
              )}

              <div>
                <label htmlFor="table-number" className="mb-1 block text-[13px] font-medium text-gray-700">Table number*</label>
                <input
                  id="table-number"
                  type="number"
                  min={1}
                  value={form.tableNumber}
                  onChange={e => setForm({ ...form, tableNumber: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                  placeholder="12"
                />
                <p className="mt-1 text-[12px] text-gray-400">Must be unique within this restaurant.</p>
              </div>

              <div>
                <label htmlFor="table-waiter" className="mb-1 block text-[13px] font-medium text-gray-700">Waiter</label>
                <select
                  id="table-waiter"
                  value={form.assignedUserId ?? ''}
                  onChange={e => setForm({ ...form, assignedUserId: e.target.value ? Number(e.target.value) : null })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                >
                  <option value="">Unassigned</option>
                  {waiters.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-[12px] text-gray-400">Select the waiter for this table.</p>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
